import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Calendar, Users, TrendingUp, Tag, MapPin } from 'lucide-react';
import { useEvents } from '../context/EventContext_supabase';
import { useGenres } from '../hooks/useGenres';
import { useVenues } from '../hooks/useVenues';
import GenreManager from '../components/GenreManager';
import VenueManager from '../components/VenueManager';
import ImageUpload from '../components/ImageUpload';
import ContactSubmissionsAdmin from '../components/ContactSubmissionsAdmin';
import supabase from '../supabase';

const AdminDashboard = () => {
  const { events, addEvent, updateEvent, deleteEvent } = useEvents();
  const { genres } = useGenres();
  const { venues } = useVenues();
  const [activeTab, setActiveTab] = useState('overview');
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editVenueId, setEditVenueId] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [existingArtists, setExistingArtists] = useState([]);
  const [artistSuggestions, setArtistSuggestions] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    venue: '',
    genres: [],
    price: '',
    image: '',
    description: '',
    artists: []
  });

  // Load existing artists from database
  useEffect(() => {
    const loadExistingArtists = async () => {
      if (!supabase) return;
      try {
        const { data: artists, error } = await supabase
          .from('artists')
          .select('*')
          .order('name');
        
        if (!error && artists) {
          setExistingArtists(artists);
        }
      } catch (err) {
        console.error('Failed to load existing artists:', err);
      }
    };
    loadExistingArtists();
  }, []);

  // ESC key handler for this component
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (lightboxImage) {
          setLightboxImage(null);
        } else if (showEventForm) {
          handleCloseForm();
        } else if (Object.keys(artistSuggestions).length > 0) {
          setArtistSuggestions({});
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [lightboxImage, showEventForm, artistSuggestions]);

  // Get artist suggestion based on name match (returns id, type, bio, social links)
  const getArtistSuggestion = (artistName) => {
    if (!artistName || artistName.trim().length < 2) return null;
    
    const matchingArtist = existingArtists.find(artist => 
      artist.name.toLowerCase() === artistName.toLowerCase().trim()
    );
    
    return matchingArtist ? {
      id: matchingArtist.id,
      type: matchingArtist.type || 'DJ',
      bio: matchingArtist.bio || '',
      socialLinks: matchingArtist.social_links || {}
    } : null;
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGenreChange = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;
    
    if (isChecked) {
      setFormData({
        ...formData,
        genres: [...formData.genres, value]
      });
    } else {
      setFormData({
        ...formData,
        genres: formData.genres.filter(genre => genre !== value)
      });
    }
  };

  const handleAddArtist = () => {
    setFormData({
      ...formData,
      artists: [
        ...formData.artists,
        {
          // Use a temporary string ID so we don't confuse it with DB numeric IDs
          id: `temp_${Date.now()}`,
          name: '',
          type: 'DJ',
          bio: ''
        }
      ]
    });
  };

  const handleRemoveArtist = (artistId) => {
    setFormData({
      ...formData,
      artists: formData.artists.filter(artist => artist.id !== artistId)
    });
  };

  const handleArtistChange = (artistId, field, value) => {
    const updatedArtists = formData.artists.map(artist => {
      if (artist.id === artistId) {
        const updatedArtist = { ...artist, [field]: value };
        // If editing a social link, update socialLinks
        if (["website","instagram","facebook","twitter","spotify","soundcloud","bandcamp","youtube"].includes(field)) {
          updatedArtist.socialLinks = { ...artist.socialLinks, [field]: value };
        }
        // If name field changed, check for existing artist data
        if (field === 'name' && value) {
          const suggestion = getArtistSuggestion(value);
          if (suggestion) {
            updatedArtist.type = suggestion.type || 'DJ';
            updatedArtist.existingId = suggestion.id; // Always set existingId for matched artist
            updatedArtist.id = suggestion.id; // Overwrite temp id with DB id for correct update
            if (suggestion.bio && !artist.bio) {
              updatedArtist.bio = suggestion.bio;
              setArtistSuggestions(prev => ({
                ...prev,
                [artistId]: suggestion
              }));
            }
            if (suggestion.socialLinks && Object.keys(suggestion.socialLinks).length > 0 && (!artist.socialLinks || Object.keys(artist.socialLinks).length === 0)) {
              updatedArtist.socialLinks = suggestion.socialLinks;
            }
          } else {
            if (updatedArtist.existingId) delete updatedArtist.existingId;
          }
        }
        
        return updatedArtist;
      }
      return artist;
    });

    setFormData({
      ...formData,
      artists: updatedArtists
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      time: '',
      venue: '',
      genres: [],
      price: '',
      image: '',
      description: '',
      artists: []
    });
  };

  const handleAddEvent = () => {
    setShowEventForm(true);
    setEditingEvent(null);
    resetForm();
  };

  const handleEditEvent = (event) => {
    console.log('Editing event:', event);
    setEditingEvent(event);
    
    // Convert genre string back to array for multi-select
    let genreArray = [];
    if (event.genres && Array.isArray(event.genres)) {
      genreArray = event.genres;
    } else if (event.genre) {
      // Split the concatenated genre string back into array
      genreArray = event.genre.split(', ').map(g => g.trim()).filter(g => g);
    }
    
    const normalizedDate = (event.date && typeof event.date === 'string' && event.date.includes('T'))
      ? event.date.split('T')[0]
      : event.date || '';

    setFormData({
      title: event.title || '',
      date: normalizedDate,
      time: event.time || '',
      venue: event.venue || '',
      genres: genreArray,
      price: event.price || event.ticketPrice || event.price_range || '',
      image: event.image_url || event.image || '',
      description: event.description || '',
      artists: event.artists && event.artists.length > 0 ? event.artists : []
    });
    setShowEventForm(true);
    setActiveTab('events');
    
    // Scroll to top of the form
    setTimeout(() => {
      const formElement = document.querySelector('[data-event-form]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleSaveEvent = async () => {
    try {
      const eventData = {
        ...formData,
        image_url: formData.image, // Save as image_url for consistency
        image: formData.image, // Keep both for compatibility
        genre: formData.genres && formData.genres.length > 0 ? formData.genres[0] : '', // First genre as primary
        genres: formData.genres || [], // Keep all genres
        ticketPrice: formData.price, // Save as ticketPrice for compatibility
        price_range: formData.price, // Save as price_range for compatibility
        price: formData.price, // Keep original price field
        artists: formData.artists || []
      };

      console.log('🔄 Saving event data:', eventData);
      console.log('Editing event ID:', editingEvent?.id);

      if (editingEvent && editingEvent.id) {
        console.log('🔄 Updating existing event with ID:', editingEvent.id);
        await updateEvent(editingEvent.id, eventData);
        console.log('✅ Event updated successfully');
      } else {
        console.log('🔄 Creating new event');
        await addEvent(eventData);
        console.log('✅ Event created successfully');
      }
      
      setShowEventForm(false);
      setEditingEvent(null);
      resetForm();
      
    } catch (error) {
      console.error('❌ Failed to save event:', error);
      alert('Failed to save event: ' + error.message);
    }
  };

  const handleDeleteEvent = (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      deleteEvent(eventId);
    }
  };

  const handleCloseForm = () => {
    setShowEventForm(false);
    setEditingEvent(null);
    resetForm();
  };

  const getUpcomingEvents = () => {
    return events
      .filter(event => new Date(event.date) >= new Date())
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Latest first
  };

  const getPastEvents = () => {
    return events
      .filter(event => new Date(event.date) < new Date())
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Latest first
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    let date;
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // Parse date-only safely as local date to avoid timezone shifts
      const [y, m, d] = dateString.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else {
      date = new Date(dateString);
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (price) => {
    if (price === '' || price === null || price === undefined) {
      return ''; // Return empty string for truly empty prices
    }
    if (price === '0' || price === 0) {
      return 'FREE';
    }
    // If price already contains $ symbol, return as is
    if (price.toString().includes('$')) {
      return price;
    }
    // Add $ symbol to numeric prices
    return `$${price}`;
  };

  // Sort artists function - bands first, then artists, DJs, producers
  const sortArtists = (artists) => {
    if (!artists) return [];
    return [...artists].sort((a, b) => {
      const typeOrder = { 'Band': 1, 'Artist': 2, 'DJ': 3, 'Producer': 4 };
      return (typeOrder[a.type] || 5) - (typeOrder[b.type] || 5);
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Handle edit venue navigation
  const handleEditVenue = (venueName) => {
    // Find venue by name
    const venue = venues.find(v => v.name === venueName);
    if (venue) {
      setEditVenueId(venue.id);
      setActiveTab('venues');
    }
  };

  // Reset editVenueId when switching away from venues tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== 'venues') {
      setEditVenueId(null);
    }
  };

  const upcomingEvents = getUpcomingEvents();
  const pastEvents = getPastEvents();

  return (
    <div style={{
      paddingTop: '120px',
      paddingBottom: '4rem',
      minHeight: '100vh',
      background: '#0a0a0f',
      color: 'white'
    }}>
      <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 2rem'}}>
        {/* Header */}
        <div style={{textAlign: 'center', marginBottom: '3rem'}}>
          <h1 style={{
            fontSize: '3rem',
            marginBottom: '1rem',
            background: 'linear-gradient(45deg, #ff0080, #7928ca)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>Admin Dashboard</h1>
          <p style={{fontSize: '1.2rem', color: '#9ca3af'}}>
            Manage your events and track performance
          </p>
        </div>

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '3rem',
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
          maxWidth: '1200px',
          margin: '0 auto 3rem'
        }}>
          <button 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: activeTab === 'overview' ? '#ff0080' : '#9ca3af',
              padding: '1rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === 'overview' ? '#ff0080' : 'transparent'}`,
              transition: 'all 0.3s ease'
            }}
            onClick={() => handleTabChange('overview')}
          >
            <TrendingUp size={20} />
            Overview
          </button>
          <button 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: activeTab === 'events' ? '#ff0080' : '#9ca3af',
              padding: '1rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === 'events' ? '#ff0080' : 'transparent'}`,
              transition: 'all 0.3s ease'
            }}
            onClick={() => handleTabChange('events')}
          >
            <Calendar size={20} />
            Events
          </button>
          <button 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: activeTab === 'genres' ? '#ff0080' : '#9ca3af',
              padding: '1rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === 'genres' ? '#ff0080' : 'transparent'}`,
              transition: 'all 0.3s ease'
            }}
            onClick={() => handleTabChange('genres')}
          >
            <Tag size={20} />
            Genres
          </button>
          <button 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: activeTab === 'venues' ? '#ff0080' : '#9ca3af',
              padding: '1rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === 'venues' ? '#ff0080' : 'transparent'}`,
              transition: 'all 0.3s ease'
            }}
            onClick={() => handleTabChange('venues')}
          >
            <MapPin size={20} />
            Venues
          </button>
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: activeTab === 'contacts' ? '#ff0080' : '#9ca3af',
              padding: '1rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === 'contacts' ? '#ff0080' : 'transparent'}`,
              transition: 'all 0.3s ease'
            }}
            onClick={() => setActiveTab('contacts')}
          >
            Contact Submissions
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '2rem',
              marginBottom: '3rem'
            }}>
              {/* Total Events Card */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(45deg, #ff0080, #7928ca)',
                  borderRadius: '12px',
                  color: 'white'
                }}>
                  <Calendar size={24} />
                </div>
                <div>
                  <h3 style={{fontSize: '2rem', fontWeight: '900', color: 'white', margin: '0 0 0.25rem 0'}}>
                    {events.length}
                  </h3>
                  <p style={{color: '#9ca3af', fontWeight: '500', margin: 0}}>Total Events</p>
                </div>
              </div>

              {/* Upcoming Events Card */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(45deg, #10b981, #059669)',
                  borderRadius: '12px',
                  color: 'white'
                }}>
                  <Calendar size={24} />
                </div>
                <div>
                  <h3 style={{fontSize: '2rem', fontWeight: '900', color: 'white', margin: '0 0 0.25rem 0'}}>
                    {upcomingEvents.length}
                  </h3>
                  <p style={{color: '#9ca3af', fontWeight: '500', margin: 0}}>Upcoming Events</p>
                </div>
              </div>

              {/* Past Events Card */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(45deg, #6b7280, #4b5563)',
                  borderRadius: '12px',
                  color: 'white'
                }}>
                  <Calendar size={24} />
                </div>
                <div>
                  <h3 style={{fontSize: '2rem', fontWeight: '900', color: 'white', margin: '0 0 0.25rem 0'}}>
                    {pastEvents.length}
                  </h3>
                  <p style={{color: '#9ca3af', fontWeight: '500', margin: 0}}>Past Events</p>
                </div>
              </div>

              {/* Revenue Card */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '2rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(45deg, #f59e0b, #d97706)',
                  borderRadius: '12px',
                  color: 'white'
                }}>
                  <TrendingUp size={24} />
                </div>
                <div>
                  <h3 style={{fontSize: '2rem', fontWeight: '900', color: 'white', margin: '0 0 0.25rem 0'}}>
                    $0
                  </h3>
                  <p style={{color: '#9ca3af', fontWeight: '500', margin: 0}}>Total Revenue</p>
                </div>
              </div>
            </div>

            {/* Recent Events */}
            {events.length > 0 && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '2rem'
              }}>
                <h2 style={{color: 'white', marginBottom: '1.5rem', fontSize: '1.5rem'}}>Recent Events</h2>
                <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                  {[...events].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5).map(event => (
                    <div key={event.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px'
                    }}>
                      <div style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9ca3af'
                      }}>
                        {event.image_url || event.image ? (
                          <img 
                            src={event.image_url || event.image} 
                            alt={event.title}
                            style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px'}}
                            onError={(e) => {
                              console.log('Overview image failed to load:', e.target.src);
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #9ca3af;"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg></div>';
                            }}
                          />
                        ) : (
                          <Calendar size={24} />
                        )}
                      </div>
                      <div style={{flex: 1}}>
                        <h4 style={{color: 'white', fontSize: '1.1rem', margin: '0 0 0.25rem 0'}}>
                          {event.title}
                        </h4>
                        <p style={{color: '#9ca3af', fontSize: '0.9rem', margin: 0}}>
                          {event.venue} • {formatDate(event.date)}
                        </p>
                      </div>
                      <div>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          background: new Date(event.date) >= new Date() ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                          color: new Date(event.date) >= new Date() ? '#10b981' : '#9ca3af'
                        }}>
                          {new Date(event.date) >= new Date() ? 'Upcoming' : 'Past'}
                        </span>
                      </div>
                      <div style={{display: 'flex', gap: '0.5rem'}}>
                        <button 
                          onClick={() => handleEditEvent(event)}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(59, 130, 246, 0.2)',
                            color: '#3b82f6'
                          }}
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteEvent(event.id)}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(239, 68, 68, 0.2)',
                            color: '#ef4444'
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <h2 style={{color: 'white', fontSize: '2rem', margin: 0}}>Manage Events</h2>
              <button 
                onClick={handleAddEvent}
                style={{
                  background: 'linear-gradient(45deg, #ff0080, #7928ca)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '1rem 2rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Plus size={20} />
                Add New Event
              </button>
            </div>

            {/* Event Form */}
            {showEventForm && (
              <div 
                data-event-form
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '2rem',
                  marginBottom: '2rem'
                }}
              >
                <h3 style={{marginBottom: '1.5rem', color: 'white'}}>
                  {editingEvent ? 'Edit Event' : 'Add New Event'}
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{marginBottom: '0.5rem', fontWeight: '600', color: 'white'}}>
                      Event Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                      placeholder="Enter event title"
                    />
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{marginBottom: '0.5rem', fontWeight: '600', color: 'white'}}>
                      Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{marginBottom: '0.5rem', fontWeight: '600', color: 'white'}}>
                      Time
                    </label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{marginBottom: '0.5rem', fontWeight: '600', color: 'white'}}>
                      Venue
                    </label>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                      <select
                        name="venue"
                        value={formData.venue}
                        onChange={handleInputChange}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: 'white',
                          fontSize: '1rem'
                        }}
                      >
                        <option value="" style={{backgroundColor: '#1a1a1f', color: 'white'}}>Select a venue</option>
                        {venues.map(venue => (
                          <option key={venue.id} value={venue.name} style={{backgroundColor: '#1a1a1f', color: 'white'}}>
                            {venue.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{marginBottom: '0.5rem', fontWeight: '600', color: 'white'}}>
                      Genres (Multiple Selection)
                    </label>
                    <div style={{
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      minHeight: '120px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '0.5rem',
                      alignItems: 'start'
                    }}>
                      {Array.isArray(genres) && genres.map(genre => (
                        <label key={typeof genre === 'object' ? genre.id : genre} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: 'white',
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            value={typeof genre === 'object' ? genre.name : genre}
                            checked={formData.genres && formData.genres.includes(typeof genre === 'object' ? genre.name : genre)}
                            onChange={handleGenreChange}
                            style={{
                              accentColor: '#7928ca',
                              transform: 'scale(1.2)'
                            }}
                          />
                          {typeof genre === 'object' ? genre.name : genre}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <label style={{marginBottom: '0.5rem', fontWeight: '600', color: 'white'}}>
                      Price
                    </label>
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                      placeholder="e.g. $25 - $45 or 0 for FREE"
                    />
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <ImageUpload
                      value={formData.image}
                      onChange={(value) => setFormData(prev => ({...prev, image: value}))}
                      placeholder="Drop event image here or click to upload"
                    />
                  </div>
                </div>

                {/* Artists/Lineup Section */}
                <div style={{
                  marginBottom: '2rem',
                  padding: '1.5rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{color: 'white', margin: 0}}>Artists/DJ Lineup</h4>
                    <button
                      type="button"
                      onClick={handleAddArtist}
                      style={{
                        background: 'linear-gradient(45deg, #10b981, #059669)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.9rem'
                      }}
                    >
                      <Plus size={16} />
                      Add Artist
                    </button>
                  </div>

                  {formData.artists && formData.artists.length > 0 ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
                      {formData.artists.map((artist, index) => (
                        <div key={artist.id || index} style={{
                          padding: '1rem',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem'
                          }}>
                            <h5 style={{color: '#ff0080', margin: 0}}>Artist {index + 1}</h5>
                            <button
                              type="button"
                              onClick={() => handleRemoveArtist(artist.id)}
                              style={{
                                background: 'rgba(239, 68, 68, 0.2)',
                                color: '#ef4444',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '6px',
                                padding: '0.25rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 120px',
                            gap: '1rem',
                            marginBottom: '1rem'
                          }}>
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '600',
                                color: 'white',
                                fontSize: '0.9rem'
                              }}>Artist Name</label>
                              <input
                                type="text"
                                value={artist.name || ''}
                                onChange={(e) => handleArtistChange(artist.id, 'name', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '6px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  color: 'white',
                                  fontSize: '0.9rem',
                                  boxSizing: 'border-box'
                                }}
                                placeholder="Enter artist name"
                              />
                            </div>
                            
                            <div>
                              <label style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: '600',
                                color: 'white',
                                fontSize: '0.9rem'
                              }}>Type</label>
                              <select
                                value={artist.type || 'DJ'}
                                onChange={(e) => handleArtistChange(artist.id, 'type', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '6px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  color: 'white',
                                  fontSize: '0.9rem',
                                  boxSizing: 'border-box'
                                }}
                              >
                                <option value="Band" style={{backgroundColor: '#1a1a1f', color: 'white'}}>Band</option>
                                <option value="Artist" style={{backgroundColor: '#1a1a1f', color: 'white'}}>Artist</option>
                                <option value="DJ" style={{backgroundColor: '#1a1a1f', color: 'white'}}>DJ</option>
                                <option value="Producer" style={{backgroundColor: '#1a1a1f', color: 'white'}}>Producer</option>
                              </select>
                            </div>
                          </div>
                          
                          <div>
                            <label style={{
                              display: 'block',
                              marginBottom: '0.5rem',
                              fontWeight: '600',
                              color: 'white',
                              fontSize: '0.9rem'
                            }}>Bio/Description</label>
                            
                            {/* Show suggestion if available */}
                            {artistSuggestions[artist.id] && artistSuggestions[artist.id].bio && (
                              <div style={{
                                marginBottom: '0.5rem',
                                padding: '0.5rem',
                                backgroundColor: 'rgba(0, 255, 128, 0.1)',
                                border: '1px solid rgba(0, 255, 128, 0.3)',
                                borderRadius: '6px',
                                fontSize: '0.8rem'
                              }}>
                                <div style={{
                                  color: '#00ff80',
                                  fontWeight: '600',
                                  marginBottom: '0.25rem'
                                }}>
                                  💡 Found existing bio for "{artist.name}":
                                </div>
                                <div style={{
                                  color: '#9ca3af',
                                  fontStyle: 'italic',
                                  marginBottom: '0.5rem'
                                }}>
                                  {artistSuggestions[artist.id].bio}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleArtistChange(artist.id, 'bio', artistSuggestions[artist.id].bio);
                                    setArtistSuggestions(prev => {
                                      const newSuggestions = { ...prev };
                                      delete newSuggestions[artist.id];
                                      return newSuggestions;
                                    });
                                  }}
                                  style={{
                                    background: 'linear-gradient(45deg, #00ff80, #00cc66)',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.7rem',
                                    cursor: 'pointer',
                                    marginRight: '0.5rem'
                                  }}
                                >
                                  Use This Bio
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setArtistSuggestions(prev => {
                                    const newSuggestions = { ...prev };
                                    delete newSuggestions[artist.id];
                                    return newSuggestions;
                                  })}
                                  style={{
                                    background: 'transparent',
                                    color: '#9ca3af',
                                    border: '1px solid rgba(156, 163, 175, 0.3)',
                                    borderRadius: '4px',
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.7rem',
                                    cursor: 'pointer'
                                  }}
                                  data-suggestion-dismiss="true"
                                >
                                  Dismiss
                                </button>
                              </div>
                            )}
                            
                            <textarea
                              value={artist.bio || ''}
                              onChange={(e) => handleArtistChange(artist.id, 'bio', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '6px',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                color: 'white',
                                fontSize: '0.9rem',
                                minHeight: '60px',
                                resize: 'vertical',
                                boxSizing: 'border-box'
                              }}
                              placeholder="Brief description about the artist..."
                              rows={2}
                            />
                          </div>

                          {/* Social Links Fields */}
                          <div style={{marginBottom: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                            <div>
                              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'white', fontSize: '0.9rem'}}>Website</label>
                              <input
                                type="text"
                                value={artist.socialLinks?.website || ''}
                                onChange={e => handleArtistChange(artist.id, 'website', e.target.value)}
                                style={{width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '0.9rem', boxSizing: 'border-box'}}
                                placeholder="https://artistwebsite.com"
                              />
                            </div>
                            <div>
                              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'white', fontSize: '0.9rem'}}>Instagram</label>
                              <input
                                type="text"
                                value={artist.socialLinks?.instagram || ''}
                                onChange={e => handleArtistChange(artist.id, 'instagram', e.target.value)}
                                style={{width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '0.9rem', boxSizing: 'border-box'}}
                                placeholder="@artistInstagram"
                              />
                            </div>
                            <div>
                              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'white', fontSize: '0.9rem'}}>Facebook</label>
                              <input
                                type="text"
                                value={artist.socialLinks?.facebook || ''}
                                onChange={e => handleArtistChange(artist.id, 'facebook', e.target.value)}
                                style={{width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '0.9rem', boxSizing: 'border-box'}}
                                placeholder="facebook.com/artist"
                              />
                            </div>
                            <div>
                              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'white', fontSize: '0.9rem'}}>Twitter</label>
                              <input
                                type="text"
                                value={artist.socialLinks?.twitter || ''}
                                onChange={e => handleArtistChange(artist.id, 'twitter', e.target.value)}
                                style={{width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '0.9rem', boxSizing: 'border-box'}}
                                placeholder="@artistTwitter"
                              />
                            </div>
                            <div>
                              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'white', fontSize: '0.9rem'}}>Bandcamp</label>
                              <input
                                type="text"
                                value={artist.socialLinks?.bandcamp || ''}
                                onChange={e => handleArtistChange(artist.id, 'bandcamp', e.target.value)}
                                style={{width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '0.9rem', boxSizing: 'border-box'}}
                                placeholder="https://artistbandcamp.com"
                              />
                            </div>
                            <div>
                              <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'white', fontSize: '0.9rem'}}>YouTube</label>
                              <input
                                type="text"
                                value={artist.socialLinks?.youtube || ''}
                                onChange={e => handleArtistChange(artist.id, 'youtube', e.target.value)}
                                style={{width: '100%', padding: '0.5rem', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '0.9rem', boxSizing: 'border-box'}}
                                placeholder="https://youtube.com/artistchannel"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '2rem',
                      color: '#9ca3af',
                      fontStyle: 'italic'
                    }}>
                      No artists added yet. Click "Add Artist" to add performers to the lineup.
                    </div>
                  )}
                </div>

                <div style={{marginBottom: '1.5rem'}}>
                  <label style={{marginBottom: '0.5rem', fontWeight: '600', color: 'white', display: 'block'}}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: 'white',
                      fontSize: '1rem',
                      minHeight: '80px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter event description"
                    rows={4}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end'
                }}>
                  <button 
                    style={{
                      background: 'rgba(239, 68, 68, 0.2)',
                      color: '#ef4444',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      cursor: 'pointer'
                    }}
                    onClick={handleCloseForm}
                    data-modal-close="true"
                  >
                    Cancel
                  </button>
                  <button 
                    style={{
                      background: 'linear-gradient(45deg, #22c55e, #16a34a)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      cursor: 'pointer'
                    }}
                    onClick={handleSaveEvent}
                  >
                    {editingEvent ? 'Update Event' : 'Save Event'}
                  </button>
                </div>
              </div>
            )}

            {/* Events List */}
            {events.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '4rem 2rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '2px dashed rgba(255, 255, 255, 0.2)',
                borderRadius: '16px'
              }}>
                <Calendar size={48} style={{color: '#6b7280', marginBottom: '1rem'}} />
                <h3 style={{color: 'white', fontSize: '1.5rem', marginBottom: '0.5rem'}}>No events yet</h3>
                <p style={{color: '#9ca3af', marginBottom: '2rem'}}>Create your first event to get started</p>
                <button 
                  onClick={handleAddEvent}
                  style={{
                    background: 'linear-gradient(45deg, #ff0080, #7928ca)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '1rem 2rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Plus size={20} />
                  Create Event
                </button>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '2rem'
              }}>
                {[...events].sort((a, b) => new Date(b.date) - new Date(a.date)).map(event => (
                  <div key={event.id} style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '200px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#9ca3af'
                    }}>
                      {event.image_url || event.image ? (
                        <img 
                          src={event.image_url || event.image} 
                          alt={event.title}
                          style={{
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => setLightboxImage({ src: event.image_url || event.image, alt: event.title })}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.02)';
                            e.target.style.boxShadow = '0 5px 20px rgba(0, 255, 128, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = 'none';
                          }}
                          onError={(e) => {
                            console.log('Image failed to load:', e.target.src);
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #9ca3af;">No Image</div>';
                          }}
                        />
                      ) : (
                        <Calendar size={48} />
                      )}
                    </div>
                    <div style={{padding: '1.5rem'}}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem'
                      }}>
                        <h3 style={{
                          color: 'white',
                          fontSize: '1.25rem',
                          margin: 0,
                          flex: 1
                        }}>{event.title}</h3>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          background: new Date(event.date) >= new Date() ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                          color: new Date(event.date) >= new Date() ? '#10b981' : '#9ca3af'
                        }}>
                          {new Date(event.date) >= new Date() ? 'Upcoming' : 'Past'}
                        </span>
                      </div>
                      <p style={{color: '#9ca3af', marginBottom: '0.5rem'}}>{typeof event.venue === 'object' ? event.venue.name : event.venue}</p>
                      <p style={{color: '#ff0080', fontWeight: '600', marginBottom: '0.5rem'}}>
                        {formatDate(event.date)} • {event.time}
                      </p>
                      <p style={{color: '#10b981', fontWeight: '700', fontSize: '1.1rem', marginBottom: '1rem'}}>
                        {formatPrice(event.price)}
                      </p>
                      
                      {/* Show artists if available */}
                      {event.artists && event.artists.length > 0 && (
                        <div style={{marginBottom: '1rem'}}>
                          <p style={{color: '#ff0080', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem'}}>
                            Lineup:
                          </p>
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                            {sortArtists(event.artists).slice(0, 3).map((artist, index) => (
                              <span key={index} style={{
                                backgroundColor: 'rgba(255, 0, 128, 0.1)',
                                color: '#ff0080',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                fontWeight: '500'
                              }}>
                                {artist.name}
                              </span>
                            ))}
                            {event.artists.length > 3 && (
                              <span style={{
                                color: '#9ca3af',
                                fontSize: '0.8rem',
                                padding: '0.25rem 0.5rem'
                              }}>
                                +{event.artists.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Show genres section */}
                      {(event.genre || (event.genres && event.genres.length > 0)) && (
                        <div style={{marginBottom: '1rem'}}>
                          <p style={{color: '#7928ca', fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.5rem'}}>
                            Genres:
                          </p>
                          <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                            {event.genres && event.genres.length > 0 ? (
                              event.genres.map((genre, index) => (
                                <span key={index} style={{
                                  backgroundColor: 'rgba(121, 40, 202, 0.1)',
                                  color: '#7928ca',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.8rem',
                                  fontWeight: '500',
                                  border: '1px solid rgba(121, 40, 202, 0.3)'
                                }}>
                                  {typeof genre === 'object' ? genre.name : genre}
                                </span>
                              ))
                            ) : event.genre ? (
                              (typeof event.genre === 'object' ? event.genre.name : event.genre).split(',').map((genre, index) => (
                                <span key={index} style={{
                                  backgroundColor: 'rgba(121, 40, 202, 0.1)',
                                  color: '#7928ca',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.8rem',
                                  fontWeight: '500',
                                  border: '1px solid rgba(121, 40, 202, 0.3)'
                                }}>
                                  {genre.trim()}
                                </span>
                              ))
                            ) : null}
                          </div>
                        </div>
                      )}
                      
                      <div style={{display: 'flex', gap: '1rem'}}>
                        <button 
                          onClick={() => handleEditEvent(event)}
                          style={{
                            background: 'rgba(59, 130, 246, 0.2)',
                            color: '#3b82f6',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Edit size={16} />
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteEvent(event.id)}
                          style={{
                            background: 'rgba(239, 68, 68, 0.2)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Genres Tab */}
        {activeTab === 'genres' && (
          <GenreManager />
        )}

        {/* Venues Tab */}
        {activeTab === 'venues' && (
          <VenueManager editVenueId={editVenueId} />
        )}

        {/* Contact Submissions Tab */}
        {activeTab === 'contacts' && (
          <ContactSubmissionsAdmin />
        )}
      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            backdropFilter: 'blur(10px)',
            animation: 'lightboxFadeIn 0.4s ease-out',
            cursor: 'pointer'
          }}
          onClick={() => setLightboxImage(null)}
          data-lightbox="true"
        >
          <style>{`
            @keyframes lightboxFadeIn {
              from { 
                opacity: 0; 
                backdrop-filter: blur(0px);
              }
              to { 
                opacity: 1; 
                backdrop-filter: blur(10px);
              }
            }
            
            @keyframes imageZoomIn {
              from { 
                opacity: 0;
                transform: perspective(1000px) rotateY(-15deg) rotateX(10deg) scale(0.8);
              }
              to { 
                opacity: 1;
                transform: perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1);
              }
            }
            
            @keyframes imageFloat {
              0%, 100% { 
                transform: perspective(1000px) translateY(0px) rotateY(0deg);
              }
              50% { 
                transform: perspective(1000px) translateY(-10px) rotateY(2deg);
              }
            }
          `}</style>
          
          <div
            style={{
              position: 'relative',
              maxWidth: '95vw',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              animation: 'imageZoomIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
              transformStyle: 'preserve-3d'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={lightboxImage.src}
              alt={lightboxImage.alt}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '15px',
                boxShadow: `
                  0 25px 50px rgba(0, 255, 128, 0.3),
                  0 15px 35px rgba(0, 255, 128, 0.2),
                  0 5px 15px rgba(0, 0, 0, 0.3),
                  inset 0 1px 0 rgba(255, 255, 255, 0.1)
                `,
                border: '2px solid rgba(0, 255, 128, 0.5)',
                animation: 'imageFloat 4s ease-in-out infinite',
                filter: 'brightness(1.1) contrast(1.05)',
                transformStyle: 'preserve-3d'
              }}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop';
              }}
            />
            
            {/* Close Button */}
            <button 
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-15px',
                right: '-15px',
                background: 'linear-gradient(135deg, #00ff80, #00cc66)',
                border: 'none',
                color: '#000000',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
                boxShadow: '0 5px 20px rgba(0, 255, 128, 0.5)',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1) rotateZ(90deg)';
                e.target.style.boxShadow = '0 8px 25px rgba(0, 255, 128, 0.8)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1) rotateZ(0deg)';
                e.target.style.boxShadow = '0 5px 20px rgba(0, 255, 128, 0.5)';
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
